import axios from "axios"
import { getBaseEndpoint } from "../../../features/endpoints/endpointManager"
import { createAsyncThunk } from "@reduxjs/toolkit"
import { RootState } from "../../../reduxStore/store"
import {
  GovTaxType,
  Practice,
  TaxInformation,
  TaxOrgType,
  TinInfo,
} from "../schema"
import { practiceLoaderSlice } from "./loader"
import {
  initialTaxInformation,
  practiceSlice,
  selectPracticeCompleteness,
} from "../model"
import { cloneDeep } from "lodash"
import { knoxKeeneComplete } from "../../ViewModel/helpers/practiceHelpers"
import { fetchBasePractice } from "./practiceInformation"
import { FileStatusDisplayIdEnum } from "../../Files/schema"
import { DataStatus } from "../../enums"
import { apiService } from "../../../features/shared/apiService"

export const taxInfoFileBlob = "taxInfoFileBlob"
// we are going to assume all processing files will upload successfully
// error handling should happen once a file returns as quarantined or in error state
export const fileStatusMap: Record<FileStatusDisplayIdEnum, UploadStatus> = {
  processing: "uploading",
  available: "success",
  quarantined: "error",
  error: "error",
}

const getTaxTablesEndpoint = (): string => {
  return getBaseEndpoint() + `/practice/taxinfotables`
}
const getAndPostTaxInfoEndpoint = (practiceId: string): string => {
  return getBaseEndpoint() + `/practice/practices/${practiceId}/taxinfo`
}
const updateTaxInfoEndpoint = (
  practiceId: string,
  taxInfoId: string,
): string => {
  return (
    getBaseEndpoint() + `/practice/practices/${practiceId}/taxinfo/${taxInfoId}`
  )
}

interface TaxTablesResponse {
  taxOrganizationTypes: TaxOrgType[]
  governmentTaxTypes: GovTaxType[]
}
export const fetchTaxTables = async (): Promise<TaxTablesResponse> => {
  const response = await axios.get<TaxTablesResponse>(getTaxTablesEndpoint())
  if (response.status === 200) {
    return response.data
  } else {
    throw new Error(
      `Failed to fetch tax tables with status code: ${response.status}`,
    )
  }
}

export const getTaxInfoTablesData = createAsyncThunk(
  "practiceProfile/taxInformation/getTaxInfoTablesData",
  async (_args, { dispatch, getState }) => {
    dispatch(
      practiceLoaderSlice.actions.practiceDataStatusUpdated({
        taxTablesDataStatus: DataStatus.SENDING,
      }),
    )

    try {
      const response = await fetchTaxTables()
      const taxOrganizationTypes: TaxOrgType[] =
        response.taxOrganizationTypes.map((type: TaxOrgType) => ({
          id: type.id,
          displayId: type.displayId,
        }))
      const governmentTaxTypes: GovTaxType[] = response.governmentTaxTypes.map(
        (type: GovTaxType) => ({
          id: type.id,
          displayId: type.displayId,
        }),
      )
      const state = getState() as RootState
      dispatch(
        practiceSlice.actions.taxInfoTablesUpdated({
          ...state.practice.taxInfoTables,
          taxOrganizationTypes: taxOrganizationTypes,
          governmentTaxTypes: governmentTaxTypes,
        }),
      )

      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          taxTablesDataStatus: DataStatus.LOADED,
        }),
      )
    } catch (error) {
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          taxTablesDataStatus: DataStatus.ERROR,
        }),
      )
      console.error(error)
    }
  },
)

interface GetTaxInfoDataThunkArgs {
  practiceId: string
  setDataStatus?: (status: DataStatus) => void
}
export const getTaxInfoData = createAsyncThunk(
  "practiceProfile/taxInformation/getTaxInfoData",
  async (
    { practiceId, setDataStatus }: GetTaxInfoDataThunkArgs,
    { dispatch, getState },
  ) => {
    dispatch(
      practiceLoaderSlice.actions.practiceDataStatusUpdated({
        taxInfoGetDataStatus: DataStatus.SENDING,
      }),
    )

    let response
    let submittedByUserId
    let newTaxInfo: TaxInformation = { ...initialTaxInformation }
    let newFile: FileType | undefined
    // get the state
    const state = getState() as RootState
    const isTaxVerified: boolean =
      state.practice.practice.taxInformation.taxVerified

    // get tax document
    try {
      setDataStatus?.(DataStatus.SENDING)

      const url: string = getAndPostTaxInfoEndpoint(practiceId)
      response = await axios.get(url)
      if (response.status === 200) {
        // TODO: add type
        const data = response.data
        submittedByUserId = data.createdBy

        newFile =
          data.fileStatus !== FileStatusDisplayIdEnum.QUARANTINED
            ? {
                id: data.fileId,
                name: data.fileName,
                uploadStatus:
                  fileStatusMap[data.fileStatus as FileStatusDisplayIdEnum],
              }
            : undefined

        newTaxInfo = {
          id: data.id,
          governmentTaxId: data.governmentTaxId,
          governmentTaxIdType: data.governmentTaxType,
          taxOrganizationType: data.taxOrganizationType,
          legalName: data.legalName,
          altName: data.alternateTaxName,
          file: newFile,
          fileUploadDate: data.createdDateTime, // UTC
          fileUploadedBy: "",
          taxVerified: isTaxVerified,
          tinValidationStatus: data.tinValidationStatus,
          irsStatus: data.irsStatus,
          tinValidatedDateTime: data.tinValidatedDateTime,
          tinValidationError: data.tinValidationError,
          tinValidatedBy: data.tinValidatedBy,
        }
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          const updatedPractice: Practice = cloneDeep({
            ...state.practice.practice,
            taxInformation: {
              ...initialTaxInformation,
            },
          })
          dispatch(practiceSlice.actions.practiceUpdated(updatedPractice))
          setDataStatus?.(DataStatus.LOADED)
        } else {
          console.error(error)
          setDataStatus?.(DataStatus.ERROR)
        }
      } else {
        console.error(
          "An unexpected error occurred while fetching tax info:",
          error,
        )
        setDataStatus?.(DataStatus.ERROR)
      }
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          taxInfoGetDataStatus: DataStatus.ERROR,
        }),
      )
      setDataStatus?.(DataStatus.ERROR)
    }

    // get userName if submittedByUserId exists
    try {
      if (submittedByUserId) {
        // call user API to get user name
        const userAccount = await apiService.account.get.USERNAME_BY_USER_ID({
          userId: submittedByUserId,
        })
        if (!!userAccount) {
          const haAdminText = state.contentManagement.content.common?.haAdmin
          const uploadedByName = userAccount.isHaUser
            ? haAdminText
            : [userAccount.firstName, userAccount.lastName]
                .filter(Boolean)
                .join(" ")
          newTaxInfo.fileUploadedBy = uploadedByName ?? ""
        }
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          const updatedPractice: Practice = cloneDeep({
            ...state.practice.practice,
            taxInformation: {
              ...initialTaxInformation,
            },
          })
          dispatch(practiceSlice.actions.practiceUpdated(updatedPractice))
          setDataStatus?.(DataStatus.LOADED)
        } else {
          console.error(error)
          setDataStatus?.(DataStatus.ERROR)
        }
      } else {
        console.error(
          "An unexpected error occurred while fetching submittedByUser for tax info file:",
          error,
        )
        setDataStatus?.(DataStatus.ERROR)
      }
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          taxInfoGetDataStatus: DataStatus.ERROR,
        }),
      )
    }

    const updatedPractice: Practice = cloneDeep({
      ...state.practice.practice,
      taxInformation: newTaxInfo,
    })
    dispatch(practiceSlice.actions.practiceUpdated(updatedPractice))
    dispatch(
      practiceLoaderSlice.actions.practiceDataStatusUpdated({
        taxInfoGetDataStatus: DataStatus.LOADED,
      }),
    )

    setDataStatus?.(DataStatus.LOADED)
    return newTaxInfo
  },
)

interface PostTaxInfoDataThunkArgs {
  practiceId: string
  payload: {
    governmentTaxId?: string
    governmentTaxType?: string
    taxOrganizationType?: string
    legalName?: string
    alternateTaxName?: string
    file?: Blob
    fileName?: string
    taxVerified?: boolean
  } & TinInfo
  setPostTaxInfoDataStatus?: (status: DataStatus) => void
}
export const postTaxInfoData = createAsyncThunk(
  "taxInformation/postTaxInfoData",
  async (
    { practiceId, payload, setPostTaxInfoDataStatus }: PostTaxInfoDataThunkArgs,
    { dispatch, getState },
  ) => {
    setPostTaxInfoDataStatus?.(DataStatus.SENDING)
    dispatch(
      practiceLoaderSlice.actions.practiceDataStatusUpdated({
        taxInfoPostDataStatus: DataStatus.SENDING,
      }),
    )

    const state = getState() as RootState

    try {
      const formData = new FormData()
      if (payload.governmentTaxId)
        formData.append("GovernmentTaxId", payload.governmentTaxId)
      if (payload.governmentTaxType)
        formData.append("GovernmentTaxType", payload.governmentTaxType)
      if (payload.taxOrganizationType)
        formData.append("TaxOrganizationType", payload.taxOrganizationType)
      if (payload.legalName) formData.append("LegalName", payload.legalName)
      if (payload.alternateTaxName)
        formData.append("AlternateTaxName", payload.alternateTaxName)
      if (payload.file) formData.append("File", payload.file, payload.fileName)
      payload.tinValidationStatus &&
        formData.append("TinValidationStatus", payload.tinValidationStatus)
      payload.irsStatus && formData.append("IrsStatus", payload.irsStatus)
      payload.tinValidatedDateTime &&
        formData.append("TinValidatedDateTime", payload.tinValidatedDateTime)
      payload.tinValidationError &&
        formData.append(
          "TinValidationError",
          payload.tinValidationError as unknown as string,
        )
      payload.tinValidatedBy &&
        formData.append("TinValidatedBy", payload.tinValidatedBy)

      const url: string = getAndPostTaxInfoEndpoint(practiceId)
      const response = await axios.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })

      if (response.status === 201) {
        const data = response.data
        const newFile: FileType | undefined =
          data.fileStatus !== FileStatusDisplayIdEnum.QUARANTINED
            ? {
                id: data.fileId,
                name: data.fileName,
                uploadStatus:
                  fileStatusMap[data.fileStatus as FileStatusDisplayIdEnum],
              }
            : undefined

        const newTaxInfo: TaxInformation = {
          id: data.id,
          governmentTaxId: data.governmentTaxId,
          governmentTaxIdType: data.governmentTaxType,
          taxOrganizationType: data.taxOrganizationType,
          legalName: data.legalName,
          altName: data.alternateTaxName,
          file: newFile,
          fileUploadDate: data.createdDateTime,
          fileUploadedBy: "",
          taxVerified: !!payload.taxVerified,
          tinValidationStatus: data.tinValidationStatus,
          irsStatus: data.irsStatus,
          tinValidatedDateTime: data.tinValidatedDateTime,
          tinValidationError: data.tinValidationError,
          tinValidatedBy: data.tinValidatedBy,
        }

        const updatedPractice: Practice = cloneDeep({
          ...state.practice.practice,
          taxInformation: newTaxInfo,
        })
        dispatch(practiceSlice.actions.practiceUpdated(updatedPractice))
        // refetch base practice IF the tax id was updated and practice agreement section is already complete
        // this will grab the agreement stale fields at the base practice level
        const { isPracticeAgreementComplete } =
          selectPracticeCompleteness(state)
        const taxIdUpdated =
          state.practice.practice.taxInformation.governmentTaxId !==
            payload.governmentTaxId ||
          state.practice.practice.taxInformation.governmentTaxIdType !==
            payload.governmentTaxType
        if (taxIdUpdated && isPracticeAgreementComplete) {
          const userId = state.user.id
          dispatch(fetchBasePractice({ practiceId, userId }))
        }
      }

      setPostTaxInfoDataStatus?.(DataStatus.LOADED)
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          taxInfoPostDataStatus: DataStatus.SAVED,
        }),
      )
      return true
    } catch (error) {
      console.error(error)
      setPostTaxInfoDataStatus?.(DataStatus.ERROR)
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          taxInfoPostDataStatus: DataStatus.ERROR,
        }),
      )
      return false
    }
  },
)

// TODO: add axios response type for this call
interface UpdateTaxInfoDataThunkArgs {
  practiceId: string
  taxInfoId: string
  payload: {
    governmentTaxId: string
    governmentTaxType: string
    taxOrganizationType: string
    legalName: string
    alternateTaxName: string
    taxVerified: boolean
  } & TinInfo
  setUpdateTaxInfoDataStatus?: (status: DataStatus) => void
}
export const updateTaxInfoData = createAsyncThunk(
  "taxInformation/updateTaxInfoData",
  async (
    {
      practiceId,
      taxInfoId,
      payload,
      setUpdateTaxInfoDataStatus,
    }: UpdateTaxInfoDataThunkArgs,
    { dispatch, getState },
  ) => {
    setUpdateTaxInfoDataStatus?.(DataStatus.SENDING)
    dispatch(
      practiceLoaderSlice.actions.practiceDataStatusUpdated({
        taxInfoPutDataStatus: DataStatus.SENDING,
      }),
    )

    const state = getState() as RootState

    try {
      const putPayload = {
        governmentTaxId: payload.governmentTaxId,
        governmentTaxType: payload.governmentTaxType,
        taxOrganizationType: payload.taxOrganizationType,
        legalName: payload.legalName,
        alternateTaxName: payload.alternateTaxName || "",
        tinValidationStatus: payload.tinValidationStatus,
        irsStatus: payload.irsStatus,
        tinValidatedDateTime: payload.tinValidatedDateTime,
        tinValidationError: payload.tinValidationError,
        tinValidatedBy: payload.tinValidatedBy,
      }

      const url: string = updateTaxInfoEndpoint(practiceId, taxInfoId)
      const response = await axios.put(url, putPayload)

      if (response.status === 200) {
        const data = response.data
        const newFile: FileType = {
          id: data.fileId,
          name: data.fileName,
          uploadStatus:
            fileStatusMap[data.fileStatus as FileStatusDisplayIdEnum],
        }

        const newTaxInfo: TaxInformation = {
          id: data.id,
          governmentTaxId: data.governmentTaxId,
          governmentTaxIdType: data.governmentTaxType,
          taxOrganizationType: data.taxOrganizationType,
          legalName: data.legalName,
          altName: data.alternateTaxName,
          file: newFile,
          fileUploadDate: data.createdDateTime,
          fileUploadedBy: "",
          taxVerified: data.taxVerified,
          tinValidationStatus: data.tinValidationStatus,
          irsStatus: data.irsStatus,
          tinValidatedDateTime: data.tinValidatedDateTime,
          tinValidationError: data.tinValidationError,
          tinValidatedBy: data.tinValidatedBy,
        }
        const updatedPractice: Practice = cloneDeep({
          ...state.practice.practice,
          taxInformation: {
            ...state.practice.practice.taxInformation,
            ...newTaxInfo,
          },
        })
        dispatch(practiceSlice.actions.practiceUpdated(updatedPractice))
        // refetch base practice IF the tax if was updated and knox keene (not practice agreement) is already complete
        // this will grab the agreement stale fields at the base practice level
        const practiceAgreementComplete = knoxKeeneComplete(
          state.practice.practice,
        )
        const taxIdUpdated =
          state.practice.practice.taxInformation.governmentTaxId !==
            payload.governmentTaxId ||
          state.practice.practice.taxInformation.governmentTaxIdType !==
            payload.governmentTaxType
        if (taxIdUpdated && practiceAgreementComplete) {
          const userId = state.user.id
          dispatch(fetchBasePractice({ practiceId, userId }))
        }
      }
      setUpdateTaxInfoDataStatus?.(DataStatus.LOADED)
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          taxInfoPutDataStatus: DataStatus.SAVED,
        }),
      )
    } catch (error) {
      console.error(error)
      setUpdateTaxInfoDataStatus?.(DataStatus.ERROR)
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          taxInfoPutDataStatus: DataStatus.ERROR,
        }),
      )
    }
  },
)

export const deleteTaxInfoData = createAsyncThunk(
  "taxInformation/deleteTaxInfoData",
  async (
    { practiceId, taxInfoId }: { practiceId: string; taxInfoId: string },
    { dispatch, getState },
  ) => {
    dispatch(
      practiceLoaderSlice.actions.practiceDataStatusUpdated({
        taxInfoDeleteDataStatus: DataStatus.SENDING,
      }),
    )

    const state = getState() as RootState
    try {
      const url: string = updateTaxInfoEndpoint(practiceId, taxInfoId)
      const response = await axios.delete(url)

      if (response.status === 204) {
        const updatedPractice: Practice = cloneDeep({
          ...state.practice.practice,
          taxInformation: { ...initialTaxInformation },
        })
        dispatch(practiceSlice.actions.practiceUpdated(updatedPractice))
      }

      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          taxInfoDeleteDataStatus: DataStatus.SAVED,
        }),
      )
    } catch (e) {
      console.error(e)
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          taxInfoDeleteDataStatus: DataStatus.ERROR,
        }),
      )
    }
  },
)
